<template>
    <div class="container">
        <div class="row">
            <div class="col-lg-12">
                <div class="p-5 pb-lg-4 bg-dark">
                    <div class="d-flex align-items-center title mb-3">
                        <router-link 
                            to="/Sports" 
                            class="btn btn-sm btn-outline-light ml-auto"
                            style="margin-right: auto !important;margin-left: 0 !important;"
                        >
                            <span class="text-light">عرض المزيد</span>
                        </router-link>
                        <h5 class="m-0 font-weight-normal">
                            <span class="title-text">العاب رياضية</span>
                            <span class="title-decoration"></span>
                        </h5>
                    </div>

                    <div v-if="isLoading" class="row">
                        <div class="col-6 col-sm-4 col-lg-4 col-xl-3 mb-4" v-for="n in 8" :key="n">
                            <div class="osahan-card">
                                <div class="game-thumbnail">
                                    <div class="skeleton-img rounded"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="error" class="text-center py-5 text-danger">
                        <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
                        <p>{{ error }}</p>
                        <button class="btn btn-primary" @click="fetchGames">إعادة المحاولة</button>
                    </div>

                    <div v-else class="row" dir="rtl">
                        <div class="col-6 col-sm-4 col-lg-4 col-xl-3 mb-4" v-for="game in sport" :key="game.id">
                            <div class="osahan-card pointer">
                                <a 
                                    class="d-block"
                                    @click="navigateToGame(game)"
                                    @mouseenter="showGameDetails(game)"
                                    @mouseleave="hideGameDetails"
                                >
                                    <div class="game-thumbnail">
                                        <img 
                                            class="img-fluid rounded"
                                            v-lazy="game.thumbnailUrl"
                                            :alt="game.title_ar"
                                            :title="game.title_ar"
                                            :src="game.isLoading ? game.placeholder : game.thumbnailUrl"
                                            @load="handleImageLoad(game)"
                                            loading="lazy"
                                        />
                                        <div v-if="isGameHovered(game)" class="game-overlay">
                                            <div class="game-info">
                                                <h6 class="text-white mb-1">{{ game.title_ar }}</h6>
                                                <p class="text-white-50 mb-0">رياضة</p>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { HTTP } from '@/Api/http-common';
import { useCookie } from 'vue-cookie-next';

export default {
    name: 'SportsHome',
    setup() {
        const router = useRouter();
        const cookie = useCookie();
        const sport = ref([]);
        const isLoading = ref(true);
        const error = ref(null);
        const hoveredGame = ref(null);

        // Add placeholder image URL
        const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiMzMzMzMyIvPjxwYXRoIGQ9Ik0xMDAgMTAwIGMwLTI1LTI1LTUwLTUwLTUwcy0yNSAwLTUwIDUwczUwIDUwIDUwLTUweiIgZmlsbD0iI0ZGRkZGRiIgb3BhY2l0eT0iMC4xIi8+PC9zdmc+';

        // Fetch games from API
        const fetchGames = async () => {
            try {
                isLoading.value = true;
                error.value = null;
                
                const response = await HTTP.get('CatogeryById.php', {
                    params: {
                        Cat_id: 'Sports',
                        LIMIT: 8
                    }
                });
                
                // Add loading state and placeholder to each game
                const games = response.data.getCatogery || [];
                sport.value = games.map(game => ({
                    ...game,
                    isLoading: true,
                    placeholder: placeholderImage
                }));
            } catch (err) {
                console.error('Error fetching games:', err);
                error.value = 'حدث خطأ أثناء تحميل الألعاب. يرجى المحاولة مرة أخرى.';
            } finally {
                isLoading.value = false;
            }
        };

        // Navigate to game details
        const navigateToGame = (game) => {
            cookie.setCookie('CID', game.category)
            router.push({ 
                name: 'GamesDetails', 
                params: { gid: game.gid }
            });
        };

        // Game hover states
        const showGameDetails = (game) => {
            hoveredGame.value = game.id;
        };

        const hideGameDetails = () => {
            hoveredGame.value = null;
        };

        const isGameHovered = (game) => {
            return hoveredGame.value === game.id;
        };

        // Handle image load
        const handleImageLoad = (game) => {
            game.isLoading = false;
        };

        // Lifecycle
        onMounted(() => {
            fetchGames();
        });

        return {
            sport,
            isLoading,
            error,
            navigateToGame,
            showGameDetails,
            hideGameDetails,
            isGameHovered,
            handleImageLoad
        };
    }
};
</script>

<style scoped>
/* Card Styles */
.osahan-card {
    transition: transform 0.3s ease;
    overflow: hidden;
    border-radius: 10px;
}

.osahan-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.game-thumbnail {
    position: relative;
    height: 200px;
    overflow: hidden;
}

.game-thumbnail img {
    object-fit: cover;
    height: 100%;
    width: 100%;
    transition: transform 0.3s ease;
}

.game-thumbnail img:hover {
    transform: scale(1.05);
}

.game-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.game-thumbnail:hover .game-overlay {
    opacity: 1;
}

.game-info {
    text-align: center;
}

.game-info h6 {
    font-weight: 600;
    margin-bottom: 5px;
}

/* Skeleton Loading */
.skeleton-img {
    height: 200px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
    background-size: 800px 104px;
    animation: shimmer 1.5s infinite;
    border-radius: 10px;
}

@keyframes shimmer {
    0% {
        background-position: -400px 0;
    }
    100% {
        background-position: 400px 0;
    }
}

/* Title Styles */
.title {
    position: relative;
    padding: 0 20px;
}

.title h5 {
    font-size: 1.5rem;
    color: #fff;
    position: relative;
    z-index: 1;
}

.title-text {
    display: inline-block;
    padding: 0 15px;
    background: linear-gradient(90deg, #000000, #292a2c);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
}

.title-decoration {
    display: inline-block;
    height: 1px;
    width: 100%;
    background: linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 100%);
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    z-index: 0;
}

/* Button Styles */
.btn-outline-light {
    color: #fff;
    border-color: #F9C90B;
}

.btn-outline-light:hover {
    background-color: #f0c82a;
    color: #000;
}

.text-light {
    color: #000 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
    .game-thumbnail {
        height: 150px;
    }

    .skeleton-img {
        height: 150px;
    }

    .title h5 {
        font-size: 1.25rem;
    }
}

/* RTL Support */
[dir="rtl"] .osahan-card {
    margin-left: 0.5rem;
    margin-right: 0;
}

[dir="rtl"] .skeleton-img {
    border-radius: 10px 0 0 10px;
}

[dir="rtl"] .title-decoration {
    right: 0;
    left: auto;
}
</style>